import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SHEETS } from "../utils/api";

const PHONE_FIELDS = [
  "Contact No",
  "Contact No ",
  "Contact No.",
  "Contact Number",
  "Contact Number ",
  "Mobile",
  "Mobile No",
  "Phone",
  "NUMBER",
  "Number",
];

// 9876543210 → "98######10"  (first 2 + 6 hidden + last 2 = 10 digits)
const maskNumber = (num) => {
  const d = String(num || "").replace(/\D/g, "").slice(-10);
  if (d.length < 4) return "";
  return `${d.slice(0, 2)}######${d.slice(-2)}`;
};

export default function GstSearchPopup({ open, onClose, mobile, onRegister }) {
  const [gst, setGst] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setGst("");
      setSearching(false);
      setResult(null);
    }
  }, [open]);

  const handleSearch = async () => {
    const g = gst.trim().toUpperCase();
    if (g.length < 10) {
      setResult({ error: "Please enter a valid GST number." });
      return;
    }
    setSearching(true);
    setResult(null);
    try {
      const res = await fetch(
        `${SHEETS.dealers()}?gst=${encodeURIComponent(g)}`,
        { cache: "no-store" }
      );
      const data = await res.json().catch(() => []);
      const rows = Array.isArray(data) ? data : [];
      if (rows.length === 0) {
        setResult({ found: false, gst: g });
      } else {
        const masked = new Set();
        let firmName = "";
        rows.forEach((r) => {
          if (!firmName) {
            firmName = String(
              r?.Particulars || r?.["Firm Name"] || r?.["Firm Name "] || ""
            ).trim();
          }
          PHONE_FIELDS.forEach((f) => {
            const m = maskNumber(r?.[f]);
            if (m) masked.add(m);
          });
        });
        setResult({ found: true, gst: g, firmName, masked: [...masked] });
      }
    } catch {
      setResult({ error: "Search failed. Please try again." });
    } finally {
      setSearching(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[500] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-900 text-lg">
            Number not registered
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Aapka number{" "}
            <span className="font-bold text-gray-900">{mobile}</span> dealer
            list mein nahi hai. Apna <b>GST number</b> search karke dekhein ki
            is firm par kaunsa number registered hai.
          </p>

          <div className="flex gap-2">
            <input
              value={gst}
              onChange={(e) =>
                setGst(e.target.value.toUpperCase().replace(/\s/g, ""))
              }
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={15}
              placeholder="Search your GST number"
              className="flex-1 min-w-0 border border-gray-300 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gray-800"
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-4 py-2.5 rounded-xl bg-gray-800 text-white font-bold text-sm hover:bg-black disabled:opacity-50 whitespace-nowrap"
            >
              {searching ? "…" : "Search"}
            </button>
          </div>

          {/* Result */}
          {result?.error && (
            <p className="text-red-600 text-sm font-semibold">{result.error}</p>
          )}

          {result?.found && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              {result.firmName && (
                <p className="text-sm font-bold text-gray-900 mb-1">
                  {result.firmName}
                </p>
              )}
              <p className="text-sm text-gray-700 mb-2">
                Ye GST already registered hai. Iss firm par registered
                number{result.masked.length > 1 ? "s" : ""}:
              </p>
              <div className="flex flex-wrap gap-2">
                {result.masked.map((m) => (
                  <span
                    key={m}
                    className="px-3 py-1.5 rounded-lg bg-white border border-emerald-300 font-mono font-bold text-emerald-800"
                  >
                    {m}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Inme se apne pure number se login karein.
              </p>
            </div>
          )}

          {result && result.found === false && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-3">
                Ye GST abhi registered nahi hai. Naye registration ke liye
                neeche form bharein.
              </p>
              <button
                onClick={() => onRegister?.(result.gst)}
                className="w-full py-2.5 rounded-xl bg-gray-800 text-white font-bold text-sm hover:bg-black"
              >
                Register New Firm →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
